import { Upload } from "tus-js-client";
import { CLOUDFLARE_ACCOUNT_ID, CLOUDFLARE_STREAM_UPLOAD_PROXY_URL } from "./CloudflareStream_Constant";

let onProgressCallback = null;

export const setOnProgressCallback = (callback) => {
    onProgressCallback = callback;
};

export const getCloudflareStreamWatchUrl = (videoId) => {
    if (!videoId) return "";
    return `https://watch.cloudflarestream.com/${videoId}`;
};

export const getCloudflareStreamApiUrl = (videoId) => {
    if (!videoId || !CLOUDFLARE_ACCOUNT_ID) return "";
    return `https://api.cloudflare.com/client/v4/accounts/${CLOUDFLARE_ACCOUNT_ID}/stream/${videoId}`;
};

export const getCloudflareStreamVideoUrl = (videoId) => getCloudflareStreamWatchUrl(videoId);

export const getCloudflareStreamEmbedUrl = (videoId) => {
    if (!videoId) return "";
    return `https://iframe.videodelivery.net/${videoId}`;
};

/**
 * Prefer Cloudflare Stream when form=video and cloudFlareVideoId is set;
 * otherwise fall back to Cloudflare-in-url, then Vimeo, then direct url.
 */
export const resolveVideoPreview = (media = {}) => {
    const form = media?.form || media?.mediaType || "";
    const isVideo = !form || form === "video";
    const cfIdFromField = typeof media?.cloudFlareVideoId === "string" ? media.cloudFlareVideoId.trim() : "";
    const cfIdFromUrl = extractCloudflareStreamId(media?.previewUrl) || extractCloudflareStreamId(media?.url);
    const cloudFlareVideoId = cfIdFromField || cfIdFromUrl || "";

    if (isVideo && cloudFlareVideoId) {
        return {
            type: "cloudflare",
            id: cloudFlareVideoId,
            embedUrl: `${getCloudflareStreamEmbedUrl(cloudFlareVideoId)}?background=1&autoplay=0`,
            watchUrl: getCloudflareStreamWatchUrl(cloudFlareVideoId),
            url: getCloudflareStreamWatchUrl(cloudFlareVideoId),
        };
    }

    const url = media?.previewUrl || media?.url || "";
    if (typeof url === "string" && url.includes("vimeo.com")) {
        const vimeoId = String(url).split("/").pop();
        return {
            type: "vimeo",
            id: vimeoId,
            embedUrl: `https://player.vimeo.com/video/${vimeoId}?background=1&autoplay=0`,
            watchUrl: url,
            url,
        };
    }

    return {
        type: "direct",
        id: null,
        embedUrl: null,
        watchUrl: url || null,
        url: url || null,
    };
};

export const extractCloudflareStreamId = (url) => {
    if (!url) return null;

    try {
        const value = String(url).trim();
        const patterns = [
            /(?:watch\.cloudflarestream\.com\/([a-zA-Z0-9]+))/i,
            /(?:iframe\.videodelivery\.net\/([a-zA-Z0-9]+))/i,
            /(?:customer-[a-z0-9]+\.cloudflarestream\.com\/([a-zA-Z0-9]+))/i,
            /(?:api\.cloudflare\.com\/client\/v4\/accounts\/[^/]+\/stream\/([a-zA-Z0-9]+))/i,
            /^([a-f0-9]{32})$/i,
        ];

        for (const pattern of patterns) {
            const match = value.match(pattern);
            if (match?.[1]) {
                return match[1];
            }
        }

        return null;
    } catch {
        return null;
    }
};

const encodeMetadataValue = (value) => btoa(unescape(encodeURIComponent(value || "")));

const getUploadFileName = (file) => (file?.name || "").trim() || "Untitled";

const buildTusUploadMetadata = (file) => {
    const name = getUploadFileName(file);
    const filetype = file.type || "video/mp4";
    return `name ${encodeMetadataValue(name)},filetype ${encodeMetadataValue(filetype)}`;
};

/**
 * Uploads a video to Cloudflare Stream using the original file name.
 *
 * @param {File} file
 * @param {string} [_ignoredTitle] kept for call-site compatibility; file.name is always used
 */
export const uploadVideoToCloudflareStream = async (file, _ignoredTitle) => {
    if (!file) {
        throw new Error("No file provided for Cloudflare Stream upload.");
    }
    if (!CLOUDFLARE_STREAM_UPLOAD_PROXY_URL) {
        throw new Error("REACT_APP_CLOUDFLARE_STREAM_UPLOAD_PROXY_URL is not configured.");
    }

    const fileName = getUploadFileName(file);
    const tusMetadataHeader = buildTusUploadMetadata(file);

    const provisionResponse = await fetch(CLOUDFLARE_STREAM_UPLOAD_PROXY_URL, {
        method: "POST",
        headers: {
            "Tus-Resumable": "1.0.0",
            "Upload-Length": String(file.size),
            "Upload-Metadata": tusMetadataHeader,
        },
    });

    const provisionData = await provisionResponse.json().catch(() => ({}));
    if (!provisionResponse.ok) {
        throw new Error(provisionData?.message || "Failed to provision Cloudflare Stream upload URL.");
    }

    const uploadUrl = provisionData?.uploadUrl;
    let streamMediaId = provisionData?.videoId || null;

    if (!uploadUrl) {
        throw new Error("Upload URL was not returned by the upload proxy.");
    }

    return new Promise((resolve, reject) => {
        const upload = new Upload(file, {
            uploadUrl,
            metadata: {
                name: fileName,
                filetype: file.type,
            },
            uploadSize: file.size,
            chunkSize: 50 * 1024 * 1024,
            retryDelays: [0, 3000, 5000, 10000, 20000],
            onError: (error) => {
                reject(error instanceof Error ? error : new Error(error?.message || "Cloudflare Stream upload failed."));
            },
            onProgress: (bytesUploaded, bytesTotal) => {
                const percent = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
                if (onProgressCallback) onProgressCallback(percent);
            },
            onAfterResponse: (_req, res) => {
                return new Promise((resolveAfterResponse) => {
                    const mediaIdHeader = res.getHeader("stream-media-id");
                    if (mediaIdHeader) streamMediaId = mediaIdHeader;
                    resolveAfterResponse();
                });
            },
            onSuccess: () => {
                if (!streamMediaId && upload.url) {
                    streamMediaId = extractCloudflareStreamId(upload.url);
                }

                if (!streamMediaId) {
                    reject(new Error("Cloudflare Stream upload did not return a video ID."));
                    return;
                }

                resolve({
                    success: true,
                    videoId: streamMediaId,
                    videoUrl: getCloudflareStreamWatchUrl(streamMediaId),
                    fileName,
                });
            },
        });

        upload.start();
    });
};
